/**
 * Book Progress Dialog
 * Weekly check-in for book/ebook consumption sessions.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { BookOpen, CheckCircle, PauseCircle, Upload } from 'lucide-react';
import { useConfirmConsumption, usePostponeReminder } from '@/hooks/useTrainingConsumption';
import { toast } from 'sonner';

interface BookProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: {
    id: string;
    artifact: {
      id: string;
      title: string;
      provider_name?: string;
    };
    technician_profile_id: string;
    progress_percent?: number | null;
  };
}

type ProgressOption = 'reading' | 'finished' | 'paused';

export function BookProgressDialog({
  open,
  onOpenChange,
  session,
}: BookProgressDialogProps) {
  const [selectedOption, setSelectedOption] = useState<ProgressOption>('reading');
  const [progressPercent, setProgressPercent] = useState(session.progress_percent || 25);
  
  const confirmMutation = useConfirmConsumption();
  const postponeMutation = usePostponeReminder();
  
  const isSubmitting = confirmMutation.isPending || postponeMutation.isPending;
  
  const handleSubmit = async () => {
    try {
      if (selectedOption === 'reading') {
        // Update progress and set next reminder for 1 week
        await confirmMutation.mutateAsync({
          sessionId: session.id,
          technicianProfileId: session.technician_profile_id,
          artifactId: session.artifact.id,
          data: {
            status: 'partial',
            progressPercent,
          },
        });
        
        // Schedule next check-in
        await postponeMutation.mutateAsync({
          sessionId: session.id,
          technicianProfileId: session.technician_profile_id,
          postponeHours: 168, // 1 week
        });
        
        toast.success('Progress saved! We\'ll check in again next week.');
      } else if (selectedOption === 'finished') {
        await confirmMutation.mutateAsync({
          sessionId: session.id,
          technicianProfileId: session.technician_profile_id,
          artifactId: session.artifact.id,
          data: {
            status: 'completed',
          },
        });
        
        toast.success('Congratulations on finishing the book! 📚');
      } else {
        // Paused - mark as partial with current progress
        await confirmMutation.mutateAsync({
          sessionId: session.id,
          technicianProfileId: session.technician_profile_id,
          artifactId: session.artifact.id,
          data: {
            status: 'partial',
            progressPercent,
          },
        });
        
        toast.info('Reading paused. Pick it up whenever you\'re ready!');
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating book progress:', error);
      toast.error('Failed to save progress. Please try again.');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Book Progress Update
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{session.artifact.title}</span>
            {session.artifact.provider_name && (
              <span className="text-muted-foreground"> - {session.artifact.provider_name}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">How far have you read?</Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[progressPercent]}
                onValueChange={([v]) => setProgressPercent(v)}
                min={5}
                max={100}
                step={5}
                className="flex-1"
                disabled={selectedOption === 'finished'}
              />
              <span className="text-sm font-medium w-12 text-right">{progressPercent}%</span>
            </div>
          </div>
          
          <RadioGroup
            value={selectedOption}
            onValueChange={(v) => {
              setSelectedOption(v as ProgressOption);
              if (v === 'finished') setProgressPercent(100);
            }}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="reading" id="reading" />
              <Label htmlFor="reading" className="flex-1 cursor-pointer flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-blue-500" />
                Still reading
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="finished" id="finished" />
              <Label htmlFor="finished" className="flex-1 cursor-pointer flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Finished!
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="paused" id="paused" />
              <Label htmlFor="paused" className="flex-1 cursor-pointer flex items-center gap-2">
                <PauseCircle className="h-4 w-4 text-muted-foreground" />
                Paused / Not continuing
              </Label>
            </div>
          </RadioGroup>
          
          {/* Future: Certificate upload */}
          <Button
            variant="outline"
            className="w-full text-muted-foreground"
            disabled
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Certificate (Coming Soon)
          </Button>
        </div>
        
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Progress'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
